import {
  workspaceAccessTag,
  type PeopleGrantMode,
  type PeoplePerson,
  type PeopleSource,
  type ResolvedPerson,
  type WorkspaceAccessResourceType,
  type WorkspaceMembershipFilter,
} from "@playbookai/types";
import type { ApiClientConfig } from "./client-config.js";
import { searchWorkspacePeople } from "./workspace-access.js";

/**
 * A canvas `PeopleSource` over the canonical resource-scoped people search.
 *
 * The module supplies what only it knows — its roster (already in memory on
 * the canvas), the search scope it may ask for, and whether the caller may add
 * someone — and this turns the canonical search's `access` into the one tag
 * spelling every module shares. No caching here: this package stays pure, and
 * the field keeps its own short cache.
 */
export function createWorkspacePeopleSource(
  config: ApiClientConfig,
  options: {
    readonly resourceType: WorkspaceAccessResourceType;
    readonly resourceId: string;
    /** `all` when the caller may see non-members, otherwise `members` (d47). */
    readonly membershipFilter: WorkspaceMembershipFilter;
    readonly grantMode: PeopleGrantMode;
    readonly roster: () => readonly PeoplePerson[];
    readonly currentUserId?: string;
    readonly resolve?: (
      ids: readonly string[],
    ) => Promise<readonly ResolvedPerson[]>;
  },
): PeopleSource {
  return {
    ...(options.currentUserId ? { currentUserId: options.currentUserId } : {}),
    grantMode: options.grantMode,
    roster: options.roster,
    ...(options.resolve ? { resolve: options.resolve } : {}),
    async search(query, { cursor, signal }) {
      const page = await searchWorkspacePeople(
        config,
        options.resourceType,
        options.resourceId,
        {
          query,
          ...(cursor ? { cursor } : {}),
          membershipFilter: options.membershipFilter,
        },
        signal,
      );
      return {
        items: page.items.map((item) => ({
          id: item.userId,
          name: item.displayName,
          email: item.primaryEmail,
          access: item.access ? workspaceAccessTag(item.access) : null,
        })),
        nextCursor: page.nextCursor,
      };
    },
  };
}

/**
 * A `PeopleSource` for a place with no access list (KPI owner, Teams, Admin,
 * Integrations): grant mode `none`, an empty roster, and whatever tenant
 * search the caller can reach. The transport is injected because the
 * browser's tenant directory route needs the shell's CSRF-aware client.
 */
export function createTenantPeopleSource(options: {
  readonly search: (
    query: string,
    cursor: string | undefined,
    signal: AbortSignal,
  ) => Promise<{
    readonly items: readonly {
      readonly id: string;
      readonly name: string;
      readonly email?: string | null;
    }[];
    readonly nextCursor: string | null;
  }>;
  readonly currentUserId?: string;
  readonly resolve?: (
    ids: readonly string[],
  ) => Promise<readonly ResolvedPerson[]>;
}): PeopleSource {
  return {
    ...(options.currentUserId ? { currentUserId: options.currentUserId } : {}),
    grantMode: { kind: "none" },
    roster: () => [],
    ...(options.resolve ? { resolve: options.resolve } : {}),
    async search(query, { cursor, signal }) {
      const page = await options.search(query, cursor, signal);
      // No access list here, so `access` stays undefined: a chip shows no tag.
      return {
        items: page.items.map((item) => ({
          id: item.id,
          name: item.name,
          ...(item.email !== undefined ? { email: item.email } : {}),
        })),
        nextCursor: page.nextCursor,
      };
    },
  };
}
